import AppPanel from '../fw/AppPanel.react';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {injectIntl, FormattedDate, FormattedTime, FormattedMessage, IntlShape} from 'react-intl';
import urlMapping from '../app/URLMapping';
import types from '../../common/model-api/types';
import {wrapNotFound} from '../app/NotFoundHandler.react';
import PagingGrid, {defaultPageSize, processPagingParams} from '../fw/PagingGrid.react';
import ChannelIcon from '../channel-defs/ChannelIcon.react';
import ScenarioIcon, {SMALL_SIZE} from '../scenarios/ScenarioIcon.react';
import UserRenderer from '../users/UserRenderer.react';
import SafeHTML from '../fw/SafeHTML.react';

import "./OrganisationEvent.scss";
import { User } from '../../common/types/types';

interface FlowEventListProps {
 intl: IntlShape;
 relay?: object;
 user: User;
}

class FlowEventList extends React.Component<FlowEventListProps> {

    typeName = 'organisationsEvents';

    static propTypes = {
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object,
        user: PropTypes.object.isRequired,
    };

    static contextTypes = {
        router: PropTypes.object
    };

    constructor(props) {
        super(props);

        this.state = {isSystemChange: false};
    }

    setIsSystemChange = (value) => {
        this.props.relay.setVariables({
            isSystemChange: value
        });
        this.setState({isSystemChange: value});
    };

    render() {
        const data = this.props.user.organisation.flow;
        let list = data.events;

        let columns = {
            'flow': {
                header: (<span></span>),
                width: '5',
                render: (column, item) => {
                    if (item.flow) {
                        if (item.flow.flowDef) {
                            return <span className="event-list"><ScenarioIcon isFlow scenario={item.flow.scenario} size={SMALL_SIZE} /></span>;
                        }

                        if (item.flow.name) {
                            return item.flow.name;
                        }
                    }

                    if (item.channel) {
                        if (item.channel.channelDef) {
                            let name = item.channel.channelDef.name;
                            if (item.channel.name) {
                                name += " - " + item.channel.name;
                            }
                            return <span className="event-list-channel"><ChannelIcon channelDef={item.channel.channelDef} size={32} /> {name}</span>;
                        }

                        if (item.channel.name) {
                            return item.channel.name;
                        }
                    }

                    return null;
                }
            },
            'author': {
                width: '10',
                render: (column, item) => {
                    if (item.author) {
                        return (<UserRenderer user={item.author} />);
                    }

                    return null;
                },
            },
            'createdAt': {
                width: '10',
                render: (column, item) => {
                    return (<small><FormattedDate value={item.createdAt}/> <FormattedTime hour='numeric' minute='numeric' second='numeric' value={item.createdAt}/></small>);
                },
            },
            'type': {
                width: '20',
            },
            'description': {
                width: '65',
                render: (column, item) => {
                    let parsed = item.changedValues;
                    if (typeof parsed === 'string' && parsed) {
                        try {
                            parsed = JSON.parse(item.changedValues);
                            if (parsed && parsed["new"]) {
                                parsed = parsed["new"];
                            }
                            parsed = JSON.stringify(parsed, null, 4);
                        } catch (e) {
                            /* ignore */
                        }
                    }
                    return (<SafeHTML className="event-list-description" title={parsed} value={item.description} />);
                },
            },
        };

        return (
            <AppPanel  breadcrumbParams={[
                {type: types['organisations'], data: this.props.user.organisation},
                {type: types['flows'], data: this.props.user.organisation.flow},
            ]} className="organisationView">

                <div className="btn-toolbar" role="toolbar">
                    <div className="btn-group" role="group" style={{"float": "right"}}>
                        <button className={"btn btn-default btn-xs" + (this.state.isSystemChange ? "" : " active")} onClick={() => this.setIsSystemChange(false)} type="button"><FormattedMessage defaultMessage="Hide system events" id="organisationEventList.hideSystemEvents" /></button>
                        <button className={"btn btn-default btn-xs" + (this.state.isSystemChange ? " active" : "")} onClick={() => this.setIsSystemChange(true)} type="button"><FormattedMessage defaultMessage="Show system events" id="organisationEventList.showSystemEvents" /></button>
                    </div>
                </div>

                <div className="event-list-grid">
                    <PagingGrid
                            columns={columns}
                            edges={list}
                            metadata={types['organisationEvents']}
                            pageInfo={list.pageInfo}
                            relay={this.props.relay}
                            renderLink={false}
                            urlMapping={urlMapping} />
                </div>

            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(FlowEventList), (props) => {
    return props.user.organisation && props.user.organisation.flow;
}), {
    initialVariables: {
        first: defaultPageSize,
        after: null,
        organisationId: '',
        flowId: '',
        isSystemChange: false,
    },
    prepareVariables (variables) {
        processPagingParams(variables);

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                featureFlags,
                organisation(id: $organisationId) {
                    id,
                    realId,
                    name,
                    description,
                    flow(id: $flowId) {
                        id,
                        realId,
                        name,
                        description,
                        events(first: $first, after: $after, isSystemChange: $isSystemChange) {
                            edges {
                                node {
                                    id,
                                    flow {
                                        id,
                                        realId,
                                        organisation_id,
                                        instance_id,
                                        selectedChannels,
                                        name,
                                        flowDef {
                                            id,
                                            realId,
                                            name,
                                        },
                                        scenario {
                                            ${ScenarioIcon.getFragment('scenario')},
                                        },
                                    },
                                    channel {
                                        id,
                                        realId,
                                        organisation_id,
                                        instance_id,
                                        name,
                                        channelDef {
                                            id,
                                            realId,
                                            name,
                                            icon
                                        }
                                    },
                                    type,
                                    description,
                                    changedValues,
                                    createdAt,
                                    isSystemChange,
                                    author {
                                        id,
                                        realId,
                                        name,
                                        picture,
                                        firstname,
                                        surname,
                                        email,
                                    },
                                }
                            },
                            pageInfo {
                                hasNextPage,
                                hasPreviousPage,
                                startCursor,
                                endCursor
                            }
                        },
                    }
                }
            }
        `,
    },
});
